import React, { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROOTS } from "@app/config/constants";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import { useUser } from "@shared/hooks/user/useUser";
import {
  getClinicNavigationItems,
  getSettingsNavigationItems,
} from "@features/clinics/utils";
import { getUserNavigationItems } from "@features/user/utils";
import {
  getEventNavigationItems,
  getEventSettingsNavigationItems,
} from "@features/events/navigation";

// Default Medentic favicon URL
const DEFAULT_FAVICON_URL:string = "https://assets.medentic.app/logos/pwa/favicon-32x32.png";

const PageMetaHandler: React.FC = () => {

  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  
  // Extract IDs from pathname as fallback when useParams doesn't work
  const pathname = location.pathname;
  const clinicIdFromPath = pathname.match(/^\/c\/([^/]+)/)?.[1];
  const eventOrganizerIdFromPath = pathname.match(/^\/e\/([^/]+)/)?.[1];
  
  // Exclude "add" from being treated as a valid entity ID
  const selectedClinicId = (params.clinicId || clinicIdFromPath) !== "add" 
    ? (params.clinicId || clinicIdFromPath) 
    : undefined;
  const selectedEventOrganizerId = (params.eventOrganizerId || eventOrganizerIdFromPath) !== "add"
    ? (params.eventOrganizerId || eventOrganizerIdFromPath)
    : undefined;

  // Get user data
  const { user } = useUser(true);

  // Get company data for clinic context
  const { company: clinicCompany } = useGetSingleCompanyDetails(selectedClinicId, {
    enabled: Boolean(selectedClinicId),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  // Get company data for event context
  const { company: eventCompany } = useGetSingleCompanyDetails(selectedEventOrganizerId, {
    enabled: Boolean(selectedEventOrganizerId),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  // Memoize navigation items to prevent unnecessary re-renders
  const navigationItems = useMemo(() => {
    try {
      if (pathname.startsWith(ROOTS.CLINIC) && selectedClinicId) {
        return [
          ...getClinicNavigationItems(t, selectedClinicId, pathname),
          ...getSettingsNavigationItems(t, selectedClinicId, pathname),
        ];
      } else if (pathname.startsWith(ROOTS.EVENTS) && selectedEventOrganizerId) {
        return [
          ...getEventNavigationItems(t, selectedEventOrganizerId, pathname),
          ...getEventSettingsNavigationItems(t, selectedEventOrganizerId, pathname),
        ];
      } else if (pathname.startsWith(ROOTS.USER)) {
        return getUserNavigationItems(t, pathname);
      }
      return [];
    } catch (error) {
      console.warn("Failed to get navigation items:", error);
      return [];
    }
  }, [pathname, selectedClinicId, selectedEventOrganizerId, t]);

  useEffect(() => {
    const updatePageMeta = () => {
      let title = "Medentic";
      let faviconUrl = DEFAULT_FAVICON_URL; // Default Medentic favicon

      // Determine context and get appropriate data
      if (pathname.startsWith(ROOTS.CLINIC)) {
        // Clinic context - use clinic company data
        if (selectedClinicId && clinicCompany) {
          const companyName = clinicCompany.businessName || clinicCompany.name || "Clinic";
          title = `${companyName} — Medentic`;
          
          // Use company logo as favicon if available, otherwise use Medentic favicon
          if (clinicCompany.avatar?.image && clinicCompany.avatar.image.trim() !== "") {
            faviconUrl = clinicCompany.avatar.image;
          } else {
            faviconUrl = DEFAULT_FAVICON_URL;
          }
        } else if (selectedClinicId) {
          title = "Clinic — Medentic";
        } else {
          title = "Clinics — Medentic";
        }
      } else if (pathname.startsWith(ROOTS.EVENTS)) {
        // Event context - use event company data
        if (selectedEventOrganizerId && eventCompany) {
          const companyName = eventCompany.businessName || eventCompany.name || "Event Organizer";
          title = `${companyName} — Medentic`;
          
          // Use company logo as favicon if available, otherwise use Medentic favicon
          if (eventCompany.avatar?.image && eventCompany.avatar.image.trim() !== "") {
            faviconUrl = eventCompany.avatar.image;
          } else {
            faviconUrl = DEFAULT_FAVICON_URL;
          }
        } else if (selectedEventOrganizerId) {
          title = "Event Organizer — Medentic";
        } else {
          title = "Events — Medentic";
        }
      } else if (pathname.startsWith(ROOTS.USER)) {
        // User context - use user data
        if (user) {
          const userName = user.firstName && user.lastName 
            ? `${user.firstName} ${user.lastName}` 
            : user.email || "User";
          title = `${userName} — Medentic`;
          
          // Use user avatar as favicon if available, otherwise use Medentic favicon
          if (user.avatar?.image && user.avatar.image.trim() !== "") {
            faviconUrl = user.avatar.image;
          } else {
            faviconUrl = DEFAULT_FAVICON_URL;
          }
        } else {
          title = "User Profile — Medentic";
        }
      } else {
        // Default context - use Medentic branding
        title = "Medentic";
        faviconUrl = DEFAULT_FAVICON_URL;
      }

      // Try to get more specific title from navigation items
      const currentRoute = navigationItems.find((item) => {
        // Check if the pathname matches the item URL exactly or ends with it
        return pathname === item.url || pathname.endsWith(item.url);
      });

      if (currentRoute) {
        // currentRoute.label is already a translated string, don't translate it again
        const routeTitle = currentRoute.label;
        // Extract company/user name from the main title
        const mainTitle = title.split(" — ")[0];
        title = `${routeTitle} — ${mainTitle}`;
      } else {
        // If no specific route found, try to get a more descriptive title based on path
        const pathSegments = pathname.split('/').filter(segment => segment);
        if (pathSegments.length > 2) {
          const lastSegment = pathSegments[pathSegments.length - 1];
          
          // Skip UUID-like segments (contains numbers and letters in a specific pattern)
          const isUuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment);
          
          if (!isUuidLike) {
            // Handle special cases like "new", "edit", etc.
            let segmentTitle = "";
            
            if (lastSegment === "new") {
              // For "new" pages, try to determine what's being created from the parent path
              const parentSegment = pathSegments[pathSegments.length - 2];
              const createMappings: Record<string, string> = {
                'attendees': 'attendeeDetails.newAttendee',
                'instructors': 'instructorDetails.createNewInstructor',
                'events': 'eventDetails.createNewEvent',
              };
              
              const translationKey = createMappings[parentSegment];
              if (translationKey) {
                segmentTitle = t(translationKey);
              } else {
                // Fallback to generic "Add [Entity]"
                const entityName = parentSegment
                  ?.split('-')
                  ?.map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  ?.join(' ') || 'Item';
                segmentTitle = t('common.add', { defaultValue: `Add ${entityName}` });
              }
            } else {
              // For other segments, capitalize and format normally
              segmentTitle = lastSegment
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            }
            
            if (segmentTitle && segmentTitle !== 'Medentic') {
              const mainTitle = title.split(" — ")[0];
              title = `${segmentTitle} — ${mainTitle}`;
            }
          }
        }
      }

      // Update document title
      document.title = title;

      // Update favicon
      updateFavicon(faviconUrl);
    };

    updatePageMeta();
  }, [
    pathname,
    selectedClinicId,
    selectedEventOrganizerId,
    clinicCompany,
    clinicCompany?.id,
    clinicCompany?.businessName,
    clinicCompany?.name,
    clinicCompany?.avatar?.image,
    eventCompany,
    eventCompany?.id,
    eventCompany?.businessName,
    eventCompany?.name,
    eventCompany?.avatar?.image,
    user,
    user?.id,
    user?.firstName,
    user?.lastName,
    user?.email,
    user?.avatar?.image,
    navigationItems,
    t,
  ]);

  return null;
};

// Helper function to update favicon
const updateFavicon = (url: string) => {
  // Remove existing favicon links
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach((favicon) => favicon.remove());

  // Ensure we have a valid URL, fallback to Medentic favicon if not
  let faviconUrl = url && url.trim() !== "" ? url : DEFAULT_FAVICON_URL;
  
  // Convert relative URLs to absolute URLs if needed
  if (faviconUrl !== DEFAULT_FAVICON_URL && !faviconUrl.startsWith("http")) {
    // If it's a relative URL, make it absolute
    if (faviconUrl.startsWith("/")) {
      faviconUrl = window.location.origin + faviconUrl;
    } else {
      // If it's a relative path without leading slash, add the base URL
      faviconUrl = window.location.origin + "/" + faviconUrl;
    }
  }


  // Create new favicon link
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/x-icon";
  link.href = faviconUrl;
  
  // Add to document head
  document.head.appendChild(link);

  // Also update apple-touch-icon for better mobile support
  const appleTouchIcon = document.createElement("link");
  appleTouchIcon.rel = "apple-touch-icon";
  appleTouchIcon.href = faviconUrl;
  document.head.appendChild(appleTouchIcon);

  // Add error handling for favicon loading
  link.onerror = () => {
    // If custom favicon fails to load, fallback to Medentic favicon
    if (faviconUrl !== DEFAULT_FAVICON_URL) {
      link.href = DEFAULT_FAVICON_URL;
      appleTouchIcon.href = DEFAULT_FAVICON_URL;
    }
  };
};

export default PageMetaHandler;
